import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { UsersIcon, ClockIcon, CheckCircleIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon } from 'lucide-react';
export function OperationalAnalyticsReport({
  filters,
  locale,
  report
}) {
  // Sample data for user productivity
  const userData = [{
    id: 'USR-001',
    name: 'John Smith',
    department: 'Finance',
    tasksCompleted: 145,
    avgProcessingTime: 2.3,
    exceptionRate: 3.2
  }, {
    id: 'USR-002',
    name: 'Sarah Johnson',
    department: 'Procurement',
    tasksCompleted: 178,
    avgProcessingTime: 1.8,
    exceptionRate: 2.1
  }, {
    id: 'USR-003',
    name: 'Michael Brown',
    department: 'Accounts Payable',
    tasksCompleted: 132,
    avgProcessingTime: 2.5,
    exceptionRate: 4.7
  }, {
    id: 'USR-004',
    name: 'Emily Davis',
    department: 'Finance',
    tasksCompleted: 156,
    avgProcessingTime: 2.0,
    exceptionRate: 2.8
  }, {
    id: 'USR-005',
    name: 'Robert Wilson',
    department: 'Procurement',
    tasksCompleted: 112,
    avgProcessingTime: 3.1,
    exceptionRate: 5.2
  }];
  // Processing time trend data
  const processingTimeTrendData = [{
    month: 'Jan',
    time: 3.2
  }, {
    month: 'Feb',
    time: 2.8
  }, {
    month: 'Mar',
    time: 2.5
  }, {
    month: 'Apr',
    time: 2.2
  }, {
    month: 'May',
    time: 1.9
  }, {
    month: 'Jun',
    time: 1.8
  }];
  // Document processing by type
  const documentProcessingData = [{
    type: 'Invoices',
    count: 280
  }, {
    type: 'Purchase Orders',
    count: 175
  }, {
    type: 'Requisitions',
    count: 120
  }, {
    type: 'Receipts',
    count: 95
  }, {
    type: 'Contracts',
    count: 45
  }];
  // System usage by module
  const moduleUsageData = [{
    module: 'AP',
    users: 42,
    transactions: 320
  }, {
    module: 'Procurement',
    users: 38,
    transactions: 280
  }, {
    module: 'Vendors',
    users: 25,
    transactions: 150
  }, {
    module: 'Reporting',
    users: 35,
    transactions: 210
  }, {
    module: 'Settings',
    users: 12,
    transactions: 45
  }];
  // Use report title and description if provided, otherwise use defaults
  const reportTitle = report?.name || 'User Productivity Report';
  const reportDescription = report?.description || 'Analysis of user productivity, process efficiency, and system performance';
  return <div className="p-6" data-id="element-15837">
      <div className="mb-8" data-id="element-15838">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-15839">{reportTitle}</h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15840">{reportDescription}</p>
      </div>
      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-15841">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15842">
          <div className="p-5" data-id="element-15843">
            <div className="flex items-center" data-id="element-15844">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-15845">
                <UsersIcon className="h-6 w-6 text-white" data-id="element-15846" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15847">
                <dl data-id="element-15848">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15849">
                    Active Users
                  </dt>
                  <dd data-id="element-15850">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15851">42</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-15852">
            <div className="text-sm" data-id="element-15853">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-15854">
                View all users
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15855">
          <div className="p-5" data-id="element-15856">
            <div className="flex items-center" data-id="element-15857">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-15858">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-15859" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15860">
                <dl data-id="element-15861">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15862">
                    Avg. Processing Time
                  </dt>
                  <dd data-id="element-15863">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15864">
                      1.8 days
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-15865">
            <div className="text-sm" data-id="element-15866">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-15867">
                View processing details
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15868">
          <div className="p-5" data-id="element-15869">
            <div className="flex items-center" data-id="element-15870">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-15871">
                <CheckCircleIcon className="h-6 w-6 text-white" data-id="element-15872" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15873">
                <dl data-id="element-15874">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15875">
                    System Uptime
                  </dt>
                  <dd data-id="element-15876">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15877">
                      99.97%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-15878">
            <div className="text-sm" data-id="element-15879">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-15880">
                View system status
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-15881">
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-15882">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15883">
            Document Processing by Type
          </h4>
          <div className="h-80" data-id="element-15884">
            <ResponsiveContainer width="100%" height="100%" data-id="element-15885">
              <BarChart data={documentProcessingData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-15886">
                <CartesianGrid strokeDasharray="3 3" data-id="element-15887" />
                <XAxis dataKey="type" data-id="element-15888" />
                <YAxis data-id="element-15889" />
                <Tooltip data-id="element-15890" />
                <Bar dataKey="count" fill="#4f46e5" data-id="element-15891" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-15892">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15893">
            Average Processing Time Trend (Days)
          </h4>
          <div className="h-80" data-id="element-15894">
            <ResponsiveContainer width="100%" height="100%" data-id="element-15895">
              <LineChart data={processingTimeTrendData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-15896">
                <CartesianGrid strokeDasharray="3 3" data-id="element-15897" />
                <XAxis dataKey="month" data-id="element-15898" />
                <YAxis data-id="element-15899" />
                <Tooltip data-id="element-15900" />
                <Line type="monotone" dataKey="time" stroke="#10b981" activeDot={{
                r: 8
              }} data-id="element-15901" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* User Productivity Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-15902">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-15903">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15904">
            User Productivity Metrics
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15905">
            Performance metrics by user for the selected time period
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-15906">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-15907">
            <thead className="bg-gray-50" data-id="element-15908">
              <tr data-id="element-15909">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15910">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15911">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15912">
                  Tasks Completed
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15913">
                  Avg. Processing Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15914">
                  Exception Rate
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15915">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-15916">
              {userData.map(user => <tr key={user.id} data-id="element-15917">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15918">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15919">
                    {user.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15920">
                    {user.tasksCompleted}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15921">
                    {user.avgProcessingTime} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15922">
                    {user.exceptionRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-15923">
                    <div className="flex items-center" data-id="element-15924">
                      {user.avgProcessingTime < 2.0 ? <TrendingUpIcon className="h-5 w-5 text-green-500 mr-1.5" data-id="element-15925" /> : user.avgProcessingTime < 2.5 ? <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-1.5" data-id="element-15926" /> : <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-1.5" data-id="element-15927" />}
                      <span className={`text-sm font-medium ${user.avgProcessingTime < 2.0 ? 'text-green-700' : user.avgProcessingTime < 2.5 ? 'text-blue-700' : 'text-yellow-700'}`} data-id="element-15928">
                        {user.avgProcessingTime < 2.0 ? 'Excellent' : user.avgProcessingTime < 2.5 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}